import { memo, useMemo, useState } from 'react';
import { NodeResizer, type NodeProps, type ResizeParams } from '@xyflow/react';
import {
  useStore,
  selectActiveNodes,
  isMechanicalItemNode,
  type MechanicalItemNode as TMechanicalItemNode,
} from '../store';
import { buildMechanicalColors, mechanicalPartKey } from '../mechanicalColors';

/**
 * A mechanical-layout footprint: a true-scale coloured rectangle, with NO text
 * inside (so dense layouts stay clean and small parts read at their real size).
 * Identity comes from the colour legend and, when selected, the fixed info
 * panel (MechanicalSelectionInfo) — never an in-box / floating label that could
 * be covered by neighbouring parts.
 */
function MechanicalItemNodeImpl({ id, data, selected }: NodeProps<TMechanicalItemNode>) {
  const setMechanicalItemField = useStore((s) => s.setMechanicalItemField);
  const [liveSize, setLiveSize] = useState<{ width: number; height: number } | null>(null);

  const scale = data.scalePxPerMm ?? 1;
  const widthMm = data.widthMm ?? 100;
  const heightMm = data.heightMm ?? 60;
  const depthMm = data.depthMm ?? 20;
  const orientation = data.orientation ?? 'top';
  const footprint = getFootprint(widthMm, heightMm, depthMm, orientation);
  const rotation = normalizeRotation(data.rotation);
  const sideways = rotation === 90 || rotation === 270;
  const displayWidthMm = sideways ? footprint.heightMm : footprint.widthMm;
  const displayHeightMm = sideways ? footprint.widthMm : footprint.heightMm;
  const width = liveSize?.width ?? Math.max(1, displayWidthMm * scale);
  const height = liveSize?.height ?? Math.max(1, displayHeightMm * scale);

  // Auto-colour by part type — identical parts share a colour (see the legend).
  const mechItems = useStore(selectActiveNodes);
  const colorByKey = useMemo(
    () => buildMechanicalColors(mechItems.filter(isMechanicalItemNode)).colorByKey,
    [mechItems],
  );
  // Manual colour wins once the user picks one (autoColor === false);
  // otherwise the auto-by-type colour is used.
  const bg =
    data.autoColor === false && data.color
      ? data.color
      : colorByKey.get(mechanicalPartKey(data)) ?? data.color ?? '#64748b';

  const label = data.label || 'Mechanical item';
  const detail = `${widthMm} x ${heightMm} mm${data.depthMm ? ` x ${data.depthMm}` : ''}`;
  const footprintDetail = `${Math.round(displayWidthMm * 10) / 10} x ${Math.round(displayHeightMm * 10) / 10} mm`;

  const commitSize = (params: ResizeParams) => {
    setLiveSize(null);
    const nextDisplayWidthMm = roundMm(params.width / scale);
    const nextDisplayHeightMm = roundMm(params.height / scale);
    const nextFootprintWidthMm = sideways ? nextDisplayHeightMm : nextDisplayWidthMm;
    const nextFootprintHeightMm = sideways ? nextDisplayWidthMm : nextDisplayHeightMm;
    const nextDims = dimensionsFromFootprint(
      widthMm,
      heightMm,
      depthMm,
      orientation,
      nextFootprintWidthMm,
      nextFootprintHeightMm,
    );
    setMechanicalItemField(id, { ...nextDims });
  };

  return (
    <div
      style={{
        width,
        height,
        position: 'relative',
        cursor: 'move',
        boxSizing: 'border-box',
        userSelect: 'none',
      }}
      title={`${label} — ${detail} — ${orientation} — footprint ${footprintDetail} — ${rotation}°`}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          border: selected ? '2px solid #2f81f7' : '1px solid rgba(15, 23, 42, 0.28)',
          borderRadius: 4,
          background: bg,
          boxShadow: selected
            ? '0 0 0 3px rgba(47, 129, 247, 0.22), 0 5px 14px rgba(15, 23, 42, 0.12)'
            : '0 1px 3px rgba(15, 23, 42, 0.1)',
          boxSizing: 'border-box',
        }}
      />
      <NodeResizer
        isVisible={selected}
        minWidth={1}
        minHeight={1}
        onResize={(_, params) =>
          setLiveSize({ width: Math.round(params.width), height: Math.round(params.height) })
        }
        onResizeEnd={(_, params) => commitSize(params)}
        lineStyle={{ borderColor: '#2f81f7' }}
        handleStyle={{
          width: 8,
          height: 8,
          borderRadius: 2,
          border: '2px solid #fff',
          background: '#2f81f7',
        }}
      />
    </div>
  );
}

function getFootprint(widthMm: number, heightMm: number, depthMm: number, orientation: unknown) {
  if (orientation === 'side') return { widthMm: depthMm, heightMm };
  if (orientation === 'front') return { widthMm, heightMm: depthMm };
  return { widthMm, heightMm };
}

function dimensionsFromFootprint(
  widthMm: number,
  heightMm: number,
  depthMm: number,
  orientation: unknown,
  footprintWidthMm: number,
  footprintHeightMm: number,
) {
  if (orientation === 'side') {
    return { widthMm, heightMm: footprintHeightMm, depthMm: footprintWidthMm };
  }
  if (orientation === 'front') {
    return { widthMm: footprintWidthMm, heightMm, depthMm: footprintHeightMm };
  }
  return { widthMm: footprintWidthMm, heightMm: footprintHeightMm, depthMm };
}

function roundMm(value: number) {
  return Math.max(1, Math.round(value * 10) / 10);
}

function normalizeRotation(value: unknown) {
  const numeric = typeof value === 'number' ? value : 0;
  return ((((Math.round(numeric / 90) * 90) % 360) + 360) % 360) as 0 | 90 | 180 | 270;
}

export const MechanicalItemNode = memo(MechanicalItemNodeImpl);
